/**
 * Loads a single Collection for the Product detail page. `notFound` is a
 * distinct flag (not folded into `error`) because a 404 has different UI
 * handling — redirect-with-banner, not an inline retry (architecture §2.3/§2.6).
 */
import { useCallback, useEffect, useState } from 'react';
import { CatalogNotFoundError, fetchCollection } from './api';
import type { Collection } from './types';

export interface UseCollection {
  data: Collection | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
  reload: () => Promise<void>;
}

export function useCollection(id: string | undefined): UseCollection {
  const [data, setData] = useState<Collection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      setData(await fetchCollection(id));
    } catch (err) {
      if (err instanceof CatalogNotFoundError) {
        setNotFound(true);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load this product.');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, loading, error, notFound, reload };
}
